import { X } from "lucide-react";

interface Props {
  name: string;
  color: string;
  size?: "xs" | "sm";
  onRemove?: () => void;
  onClick?: () => void;
  active?: boolean;
  title?: string;
}

// Light contrast helper: returns true when the chip background is dark
// enough that white text reads better than the dim foreground.
function isDarkColor(hex: string): boolean {
  const c = hex.replace("#", "");
  if (c.length !== 6) return true;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  // Perceived luminance — anything under ~0.55 we treat as "dark".
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.6;
}

export function LabelBadge({
  name,
  color,
  size = "xs",
  onRemove,
  onClick,
  active,
  title,
}: Props) {
  const dark = isDarkColor(color);
  const bg = active ? color : `${color}22`;
  const fg = active ? (dark ? "#fff" : "#0F172A") : color;
  const border = active ? color : `${color}55`;
  const padding = size === "xs" ? "px-2 py-0.5" : "px-2.5 py-1";
  const fontSize = size === "xs" ? "text-[10px]" : "text-xs";

  const interactive = !!onClick;
  const Comp = interactive ? "button" : "span";

  return (
    <Comp
      type={interactive ? "button" : undefined}
      onClick={onClick}
      title={title ?? name}
      className={`inline-flex items-center gap-1 ${padding} ${fontSize} font-medium rounded-full border transition-colors ${
        interactive ? "hover:opacity-80 cursor-pointer" : ""
      }`}
      style={{
        backgroundColor: bg,
        color: fg,
        borderColor: border,
      }}
      data-testid="label-badge"
    >
      <span className="truncate max-w-[140px]">{name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="opacity-60 hover:opacity-100 -mr-1"
          aria-label={`Retirer l'étiquette ${name}`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </Comp>
  );
}

export default LabelBadge;
